/**
 * Print Type "choose then reveal" interaction for the 2-option (Print Type x
 * Size) pilot product pages.
 *
 * Clicking a Print Type choice hides the other choices, swaps the main
 * product gallery's room mockups for a 3-photo gallery of that print type's
 * real product shots, and shows a description of the chosen print type plus
 * a Back control that restores both. Falls back to a "coming soon"
 * placeholder for any option value that has no `images` array in the JSON
 * block this script reads from (`[data-print-type-copy]` in
 * snippets/product-variant-picker.liquid) - e.g. Digital Download.
 *
 * Dawn's own <variant-selects> element re-fetches and replaces its markup
 * on every option change (to refresh price/availability), which would
 * otherwise silently wipe our hide/show state and reset an open detail
 * panel back to the choice list. Worse, that markup swap can re-insert (and
 * re-execute) this very script tag, so state and the "listeners attached"
 * flag both live on `window` - a plain closure variable would reset to
 * empty on every re-execution and immediately stomp the real selection.
 */
(function () {
  var PRINT_TYPE_STATE = window.__printTypeDetailState || (window.__printTypeDetailState = Object.create(null));

  function buildPlaceholder(label) {
    var el = document.createElement('div');
    el.className = 'print-type-detail__placeholder-shot';
    el.setAttribute('data-print-type-placeholder', '');
    el.innerHTML =
      '<svg viewBox="0 0 24 24" width="32" height="32" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M4 5h3.2l1.1-1.6c.2-.3.5-.4.8-.4h5.8c.3 0 .6.1.8.4L16.8 5H20a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm8 3a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6Z"/>' +
      '</svg>' +
      '<span>' + label + '<br>product photo coming soon</span>';
    return el;
  }

  function buildGallery(images, label) {
    var el = document.createElement('div');
    el.className = 'print-type-detail__gallery';
    el.setAttribute('data-print-type-gallery', '');
    images.forEach(function (src, i) {
      var img = document.createElement('img');
      img.src = src;
      img.alt = label + ' print sample ' + (i + 1);
      img.loading = 'lazy';
      el.appendChild(img);
    });
    return el;
  }

  // Real photos when the copy JSON has them for this value, otherwise the
  // original "coming soon" placeholder (kept for any future print type that
  // doesn't have shots yet, e.g. Digital Download).
  function buildMedia(info, label) {
    if (info && info.images && info.images.length) {
      return buildGallery(info.images, label);
    }
    return buildPlaceholder(label);
  }

  function sectionIdFor(picker) {
    var section = picker.closest('[data-section]');
    return section ? section.getAttribute('data-section') : 'default';
  }

  // The media gallery (.product__media-wrapper) is a SIBLING of the info
  // column, not a descendant of <variant-selects> - and <variant-selects>
  // also carries its own data-section, which is nearer than the outer
  // <product-info> wrapper. So scope the media lookup from <product-info>
  // (the whole-section custom element in main-product.liquid), not from
  // whatever closest('[data-section]') happens to match.
  function productRootFor(picker) {
    return picker.closest('product-info') || document;
  }

  function readCopy(picker) {
    var copyScript = picker.querySelector('[data-print-type-copy]');
    if (!copyScript) return {};
    try {
      return JSON.parse(copyScript.textContent) || {};
    } catch (err) {
      return {};
    }
  }

  function applyState(picker) {
    var choicesFieldset = picker.querySelector('[data-print-type-choices]');
    var detail = picker.querySelector('[data-print-type-detail]');
    if (!choicesFieldset || !detail) return;

    var mediaEl = picker.querySelector('[data-print-type-detail-media]');
    var titleEl = picker.querySelector('[data-print-type-detail-title]');
    var descEl = picker.querySelector('[data-print-type-detail-description]');
    var productRoot = productRootFor(picker);
    var mediaWrapper = productRoot.querySelector('.product__media-wrapper');
    var overlay = productRoot.querySelector('[data-print-type-gallery-overlay]');

    var selected = PRINT_TYPE_STATE[sectionIdFor(picker)];

    if (selected) {
      var info = readCopy(picker)[selected] || {};

      if (titleEl) titleEl.textContent = selected;
      if (descEl) descEl.textContent = info.description || '';
      if (mediaEl) {
        mediaEl.innerHTML = '';
        mediaEl.appendChild(buildMedia(info, selected));
      }

      choicesFieldset.hidden = true;
      detail.hidden = false;

      // Digital Download (or any choice with no product shots): keep the
      // room mockups showing and show no "coming soon" placeholder
      // (Thomas, Sept 25 2026).
      var hasShots = !!(info && info.images && info.images.length);
      if (mediaEl && !hasShots) mediaEl.innerHTML = '';
      if (mediaWrapper && !hasShots) {
        mediaWrapper.hidden = false;
        if (overlay) overlay.remove();
      } else if (mediaWrapper) {
        mediaWrapper.hidden = true;

        // Always rebuild the overlay from scratch on a selection change -
        // it may need to switch between a placeholder and a real gallery (or
        // between two different galleries), which is simpler and safer than
        // trying to mutate whichever node type happens to be there already.
        if (overlay) overlay.remove();

        overlay = buildMedia(info, selected);
        overlay.setAttribute('data-print-type-gallery-overlay', '');
        overlay.classList.add('print-type-detail__gallery-overlay');
        mediaWrapper.insertAdjacentElement('afterend', overlay);
      }
    } else {
      choicesFieldset.hidden = false;
      detail.hidden = true;
      if (mediaWrapper) mediaWrapper.hidden = false;
      if (overlay) overlay.hidden = true;
    }
  }

  function handleChange(event) {
    var input = event.target;
    if (!input || input.type !== 'radio') return;

    var fieldset = input.closest('[data-print-type-choices]');
    if (!fieldset) return;

    var picker = fieldset.closest('[data-print-type-picker]');
    if (!picker) return;

    PRINT_TYPE_STATE[sectionIdFor(picker)] = input.value;
    applyState(picker);
  }

  function handleClick(event) {
    var backBtn = event.target.closest('[data-print-type-back]');
    if (!backBtn) return;

    var picker = backBtn.closest('[data-print-type-picker]');
    if (!picker) return;

    PRINT_TYPE_STATE[sectionIdFor(picker)] = null;
    applyState(picker);
  }

  var observer = window.__printTypeDetailObserver || null;

  function reapplyAll() {
    // applyState mutates the DOM (rebuilding the media, inserting the
    // gallery overlay); without pausing observation those mutations would
    // immediately re-trigger this same MutationObserver callback, forever.
    if (observer) observer.disconnect();

    document.querySelectorAll('[data-print-type-picker]').forEach(function (picker) {
      if (PRINT_TYPE_STATE[sectionIdFor(picker)]) {
        applyState(picker);
      }
    });

    if (observer) observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Guard against attaching duplicate listeners/observers if this script
  // re-executes (see note above on window.__printTypeDetailState).
  if (!window.__printTypeDetailListenersAttached) {
    window.__printTypeDetailListenersAttached = true;

    // Capture phase so our hide/show runs before Dawn's own bubble-phase
    // variant-change handling starts its async re-render.
    document.addEventListener('change', handleChange, true);
    document.addEventListener('click', handleClick);

    // Dawn swaps in fresh server-rendered markup for the variant picker
    // after every change (new DOM nodes, state reset to defaults) -
    // reapply ours whenever that happens.
    observer = new MutationObserver(reapplyAll);
    window.__printTypeDetailObserver = observer;
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  reapplyAll();
})();
